// Warenkorb als React-Context. Reiner Client-State (keine Preislogik) -
// die Summe ist nur eine Anzeige, verbindlich rechnet Stripe im Checkout.
// Der Warenkorb liegt in localStorage und uebersteht so einen Reload bzw. den
// Login-Redirect (Keycloak).
//
// Mengen: jede Position genau 1x. Eine Navigator-Lizenz gilt pro E-Mail-Adresse;
// mehrere Lizenzen laufen als Anfrage ans Navigator-Team (Entscheidung 22.09.2026).
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react"
import type { CartLine, CatalogItem, SelectedPackage } from "./types"

interface CartCtx {
  lines: CartLine[]
  count: number
  subtotalNet: number
  add: (item: CatalogItem, selectedPackages?: SelectedPackage[]) => void
  remove: (priceId: string) => void
  clear: () => void
}

const Ctx = createContext<CartCtx | null>(null)

const STORAGE_KEY = "sinus-access-cart"

function loadInitial(): CartLine[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    // Grobe Validierung: nur Eintraege mit Produkt + Menge uebernehmen.
    return parsed.filter(
      (l: unknown): l is CartLine =>
        !!l && typeof l === "object" && "item" in l && "quantity" in l,
    )
  } catch {
    return []
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [lines, setLines] = useState<CartLine[]>(loadInitial)

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(lines))
    } catch {
      /* localStorage nicht verfuegbar -> Warenkorb bleibt nur im Speicher. */
    }
  }, [lines])

  function add(item: CatalogItem, selectedPackages?: SelectedPackage[]) {
    setLines((prev) => {
      const existing = prev.find((l) => l.item.price_id === item.price_id)
      if (existing) {
        // Bereits im Warenkorb: nur die Paketauswahl aktualisieren.
        return prev.map((l) =>
          l.item.price_id === item.price_id
            ? { ...l, quantity: 1, selectedPackages: selectedPackages ?? l.selectedPackages }
            : l,
        )
      }
      return [...prev, { item, quantity: 1, selectedPackages }]
    })
  }

  function remove(priceId: string) {
    setLines((prev) => prev.filter((l) => l.item.price_id !== priceId))
  }

  function clear() {
    setLines([])
  }

  const value = useMemo<CartCtx>(() => {
    const count = lines.length
    const subtotalNet = lines.reduce((s, l) => s + (l.item.amount_net ?? 0), 0)
    return { lines, count, subtotalNet, add, remove, clear }
  }, [lines])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useCart(): CartCtx {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error("useCart muss innerhalb von <CartProvider> genutzt werden.")
  return ctx
}
